import { hasSupabaseImageVariants } from "@/lib/supabase-image";

/**
 * Image Compression Utility
 * Compresses images to WebP format on the client side for reduced file size
 */

export interface CompressOptions {
  maxWidth?: number;
  maxHeight?: number;
  /**
   * 긴 변 상한. 지정하면 maxWidth/maxHeight 대신 이것만 적용한다(방향 무관).
   * maxWidth 1920 × maxHeight 1080 은 가로 기준이라 세로 사진(폰 촬영 3:4)의
   * 긴 변을 1080 으로 깎는다 — 신분증처럼 원본 해상도가 중요한 곳은 이걸 쓴다.
   */
  maxLongEdge?: number;
  quality?: number; // 0 ~ 1
}

/**
 * 비율을 유지한 채 상한 안에 들어가는 크기를 계산한다(확대는 하지 않음).
 * maxLongEdge 가 있으면 그것만, 없으면 기존 maxWidth → maxHeight 순서 규칙.
 */
export function fitDimensions(
  width: number,
  height: number,
  { maxWidth, maxHeight, maxLongEdge }: Pick<CompressOptions, "maxWidth" | "maxHeight" | "maxLongEdge">
): { width: number; height: number } {
  if (maxLongEdge) {
    const long = Math.max(width, height);
    if (long <= maxLongEdge) return { width, height };
    const r = maxLongEdge / long;
    return { width: Math.round(width * r), height: Math.round(height * r) };
  }
  if (maxWidth && width > maxWidth) {
    height = (height * maxWidth) / width;
    width = maxWidth;
  }
  if (maxHeight && height > maxHeight) {
    width = (width * maxHeight) / height;
    height = maxHeight;
  }
  return { width, height };
}

const DEFAULT_OPTIONS: CompressOptions = {
  maxWidth: 1920,
  maxHeight: 1080,
  quality: 0.85,
};

/**
 * Compress image file to WebP format
 * @param file Original image file
 * @param options Compression options
 * @returns Compressed WebP file
 */
export async function compressImageToWebP(
  file: File,
  options: CompressOptions = {}
): Promise<File> {
  const merged = { ...DEFAULT_OPTIONS, ...options };
  const { quality } = merged;
  // maxLongEdge 를 쓰면 기본값의 가로 기준 상한(1920×1080)은 적용하지 않는다.
  const bounds = options.maxLongEdge
    ? { maxLongEdge: options.maxLongEdge }
    : { maxWidth: merged.maxWidth, maxHeight: merged.maxHeight };

  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const img = new Image();

      img.onload = () => {
        // Resize while maintaining aspect ratio
        const { width, height } = fitDimensions(img.width, img.height, bounds);

        // Draw on canvas
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext("2d");
        if (!ctx) {
          reject(new Error("Canvas context not available"));
          return;
        }

        ctx.drawImage(img, 0, 0, width, height);

        // Convert to WebP. iOS Safari can silently return a PNG blob when it
        // chooses not to honor the WebP request (e.g. large HDR HEIC originals,
        // low memory). Trust `blob.type` as the source of truth and fall back
        // to JPEG if WebP wasn't produced — canvas.toBlob with "image/jpeg" is
        // universally supported. Compression intent (resize + re-encode) is
        // preserved either way.
        const originalName = file.name.replace(/\.[^/.]+$/, "");
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error("Failed to compress image"));
              return;
            }

            if (blob.type === "image/webp") {
              resolve(
                new File([blob], `${originalName}.webp`, { type: blob.type })
              );
              return;
            }

            // WebP not produced — retry as JPEG with the same quality. This
            // path protects against iOS Safari silent PNG fallback which would
            // otherwise reach the server with a lying Content-Type.
            canvas.toBlob(
              (jpegBlob) => {
                if (!jpegBlob) {
                  reject(
                    new Error("Failed to compress image (jpeg fallback)")
                  );
                  return;
                }
                resolve(
                  new File([jpegBlob], `${originalName}.jpg`, {
                    type: jpegBlob.type || "image/jpeg",
                  })
                );
              },
              "image/jpeg",
              quality
            );
          },
          "image/webp",
          quality
        );
      };

      img.onerror = () => {
        reject(new Error("Failed to load image"));
      };

      img.src = e.target?.result as string;
    };

    reader.onerror = () => {
      reject(new Error("Failed to read file"));
    };

    reader.readAsDataURL(file);
  });
}

/**
 * Compress multiple image files to WebP
 * @param files Original image file array
 * @param options Compression options
 * @returns Compressed WebP file array
 */
export async function compressImagesToWebP(
  files: File[],
  options: CompressOptions = {}
): Promise<File[]> {
  return Promise.all(files.map((file) => compressImageToWebP(file, options)));
}

/**
 * Format file size to human readable format
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
}

/**
 * Check if file is an image
 */
export function isImageFile(file: File): boolean {
  return file.type.startsWith("image/");
}

/**
 * Check if file is a video
 */
export function isVideoFile(file: File): boolean {
  return file.type.startsWith("video/");
}

// ============================================
// Image Placeholder Utilities for Next.js Image
// ============================================

/**
 * Shimmer effect for image placeholder
 * Returns a base64 encoded SVG with shimmer animation
 */
const shimmer = (w: number, h: number) => `
<svg width="${w}" height="${h}" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
  <defs>
    <linearGradient id="g">
      <stop stop-color="#f6f7f8" offset="0%" />
      <stop stop-color="#edeef1" offset="20%" />
      <stop stop-color="#f6f7f8" offset="40%" />
      <stop stop-color="#f6f7f8" offset="100%" />
    </linearGradient>
  </defs>
  <rect width="${w}" height="${h}" fill="#f6f7f8" />
  <rect id="r" width="${w}" height="${h}" fill="url(#g)" />
  <animate xlink:href="#r" attributeName="x" from="-${w}" to="${w}" dur="1s" repeatCount="indefinite"  />
</svg>`;

/**
 * Convert string to base64
 */
const toBase64 = (str: string) =>
  typeof window === "undefined"
    ? Buffer.from(str).toString("base64")
    : window.btoa(str);

/**
 * Get shimmer placeholder for images
 * Use this as blurDataURL for Next.js Image component
 *
 * @example
 * <Image
 *   src={imageUrl}
 *   placeholder="blur"
 *   blurDataURL={getShimmerPlaceholder(400, 400)}
 * />
 */
export function getShimmerPlaceholder(width = 400, height = 400): string {
  return `data:image/svg+xml;base64,${toBase64(shimmer(width, height))}`;
}

/**
 * Simple gray placeholder (smaller than shimmer)
 * Use for minimal data transfer
 */
export const BLUR_PLACEHOLDER =
  "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjZmN2Y4Ii8+PC9zdmc+";

/**
 * Pre-computed shimmer placeholder for common sizes
 */
export const SHIMMER_PLACEHOLDER = getShimmerPlaceholder(400, 400);

// ============================================
// Image Optimization Skip Utilities
// ============================================

/**
 * Check if URL is from Supabase Storage
 * Supabase Storage 이미지는 업로드 시 이미 WebP 압축되어 있음
 */
export function isSupabaseStorageUrl(url: string): boolean {
  return url.includes("supabase.co/storage/v1/object/public/");
}

/**
 * Determine if image should skip Next.js optimization
 * 다음 경우 최적화 스킵:
 * - Blob URLs (업로드 프리뷰)
 * - SVG 파일 (벡터 이미지)
 *
 * Note: Supabase Storage 이미지는 여기서 스킵 대상이 아닙니다.
 * OptimizedImage 가 custom loader 로 업로드 시 만들어 둔 변형 파일
 * (-w480/-w960.webp, 그 이상은 원본)을 직접 가리키므로 srcset/sizes 는
 * 유지되면서 /_next/image·Supabase 이미지 변환 과금은 발생하지 않습니다.
 */
export function shouldSkipOptimization(src: string | null | undefined): boolean {
  if (!src) return false;
  return src.startsWith("blob:") || src.endsWith(".svg");
}

export type ImageDelivery = "supabase-variant" | "unoptimized" | "vercel";

/**
 * Decide how <OptimizedImage> serves a source (single source of truth for the
 * "who pays for this image" question):
 *
 * - Supabase Storage public objects that can have pre-generated variants →
 *   variant loader (-w480/-w960.webp, original above 960). No transformation
 *   endpoint, no `/_next/image`.
 * - Everything else — local /public assets, blob/data previews, SVG/GIF,
 *   already-variant storage URLs, and any third-party host (e.g. unsplash,
 *   already sized by its own CDN params) — is served as-is.
 * - `forceOptimize` is the only way onto the Vercel optimizer.
 */
export function resolveImageDelivery(
  src: string,
  forceOptimize = false,
): ImageDelivery {
  if (forceOptimize) return "vercel";
  if (hasSupabaseImageVariants(src)) return "supabase-variant";
  return "unoptimized";
}
